import { useCallback, useEffect, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getCatalogProductById } from './catalog';
import type { ProductDetailDto } from '@/services/platform/api/model';

export interface UseShopProductResult {
  data: ProductDetailDto | undefined;
  isLoading: boolean;
  hasFetched: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetches full product detail for the given public GUID.
 * Mirrors the plain state-and-effect pattern used by `useShopProducts`.
 * Initial `isLoading` is true whenever a productId is provided so callers can
 * show a spinner instead of a not-found state on first render.
 */
export function useShopProduct(productId?: string): UseShopProductResult {
  const locale = useLocaleStore(state => state.locale);
  const [data, setData] = useState<ProductDetailDto | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(!!productId);
  const [hasFetched, setHasFetched] = useState(false);

  const refetch = useCallback(async () => {
    if (!productId) {
      setData(undefined);
      setIsLoading(false);
      setHasFetched(true);
      return;
    }
    setIsLoading(true);
    try {
      const product = await getCatalogProductById(productId, locale);
      setData(product);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  }, [productId, locale]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, hasFetched, refetch };
}
